import { defineModule } from '../utils/helpers';

interface LoadMoreResponse {
  html: string;
  has_more: boolean;
}

// Start fetching well before the sentinel is visible, so the next batch is
// usually in place by the time the visitor reaches the end of the grid.
const ROOT_MARGIN = '600px 0px';

const REQUEST_TIMEOUT = 10000;

const TEARDOWN = 'teardown';

let observer: IntersectionObserver | null = null;
const inFlight = new Set<AbortController>();

const isLoadMoreResponse = (value: unknown): value is LoadMoreResponse =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as LoadMoreResponse).html === 'string' &&
  typeof (value as LoadMoreResponse).has_more === 'boolean';

const getElements = (container: HTMLElement) => ({
  button: container.querySelector<HTMLButtonElement>('[data-load-more-button]'),
  status: container.querySelector<HTMLElement>('[data-load-more-status]'),
  target: document.querySelector<HTMLElement>(container.dataset.target ?? ''),
});

const fetchBatch = async (container: HTMLElement, signal: AbortSignal) => {
  const url = new URL(container.dataset.endpoint ?? '', window.location.href);
  url.searchParams.set('offset', container.dataset.offset ?? '0');
  url.searchParams.set('post_type', container.dataset.postType ?? 'post');
  url.searchParams.set('category', container.dataset.category ?? '0');

  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);

  const data: unknown = await response.json();
  if (!isLoadMoreResponse(data)) throw new Error('Malformed batch response');

  return data;
};

const loadBatch = async (container: HTMLElement) => {
  const { button, status, target } = getElements(container);
  if (!target || container.dataset.loading === 'true') return;

  container.dataset.loading = 'true';
  button?.classList.add('hidden');
  if (status) status.textContent = container.dataset.loadingLabel ?? '';

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  inFlight.add(controller);

  try {
    const data = await fetchBatch(container, controller.signal);

    // Swup swaps .page-wrapper mid-flight, so a continuation that resumes after
    // a navigation holds the old container and grid. Appending to them puts the
    // batch in a detached tree and hands the live observer a node that can never
    // intersect.
    if (!container.isConnected) return;

    const fragment = document.createRange().createContextualFragment(data.html);

    if (data.has_more && !fragment.children.length) {
      throw new Error('Empty batch claiming more posts');
    }

    const offset = Number(container.dataset.offset ?? 0);

    container.dataset.offset = `${offset + fragment.children.length}`;
    target.append(fragment);

    if (!data.has_more) {
      observer?.unobserve(container);
      container.remove();
      return;
    }

    if (status) status.textContent = '';

    // The sentinel can still be in view on a tall viewport — re-observing makes
    // the observer re-evaluate it and queue the next batch straight away.
    observer?.unobserve(container);
    observer?.observe(container);
  } catch {
    // A teardown abort is not a failed batch: cleanup has already unbound the
    // retry handlers, so revealing the button would offer a control that does
    // nothing on a page the visitor may still be looking at.
    if (controller.signal.reason === TEARDOWN) {
      if (status) status.textContent = '';

      return;
    }

    // Unobserved rather than merely not re-observed: leaving it observed means
    // scrolling past the rootMargin and back queues another batch against a
    // failing endpoint. A failed batch waits for the button, which re-observes
    // through the success path above once one comes back.
    observer?.unobserve(container);
    if (status) status.textContent = container.dataset.errorLabel ?? '';
    button?.classList.remove('hidden');
  } finally {
    clearTimeout(timer);
    inFlight.delete(controller);
    delete container.dataset.loading;
  }
};

const handleIntersect: IntersectionObserverCallback = (entries) => {
  entries
    .filter((entry) => entry.isIntersecting)
    .forEach((entry) => loadBatch(entry.target as HTMLElement));
};

const handleRetry = (event: Event) => {
  const container = (event.currentTarget as HTMLElement).closest<HTMLElement>(
    '[data-load-more]',
  );
  if (container) loadBatch(container);
};

export default defineModule(
  () => {
    const containers =
      document.querySelectorAll<HTMLElement>('[data-load-more]');
    if (!containers.length) return;

    observer = new IntersectionObserver(handleIntersect, {
      rootMargin: ROOT_MARGIN,
    });

    containers.forEach((container) => {
      observer?.observe(container);
      container
        .querySelector('[data-load-more-button]')
        ?.addEventListener('click', handleRetry);
    });
  },
  () => {
    inFlight.forEach((controller) => controller.abort(TEARDOWN));
    inFlight.clear();

    observer?.disconnect();
    observer = null;

    document
      .querySelectorAll<HTMLElement>('[data-load-more-button]')
      .forEach((button) => button.removeEventListener('click', handleRetry));
  },
);
